import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AlertController, IonicModule, IonModal } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import {
  PROJECT_MEMBER_ROLE_ICONS,
  PROJECT_MEMBER_ROLE_NAMES,
  PROJECT_MEMBER_ROLES,
  ProjectMemberRole,
} from 'src/app/constants';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { WhereClause } from 'src/models';
import { Entity, ENTITY_TYPES, EntityType } from 'src/models/entities';
import { ProjectMember } from 'src/models/projects';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    IonicModule,
    FormsModule,
    AvatarModule,
    NgxTippyModule,
  ],
  selector: 'app-project-members-list',
  templateUrl: './project-members-list.component.html',
  styleUrls: ['./project-members-list.component.scss'],
})
export class ProjectMembersListComponent implements OnInit {
  @ViewChild('addMemberModal') addMemberModal: IonModal;

  projectId: string;
  projectMembers: Partial<ProjectMember>[] = [];
  projectMember: Partial<ProjectMember>;
  canEditMembers = false;

  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;

  sortField = 'name';
  sortDesc = false;
  limit = 25;
  searchText: string;

  newMember?: Partial<Entity>;
  newMemberEntityType?: EntityType;
  newMemberRole: ProjectMemberRole = PROJECT_MEMBER_ROLES.STANDARD;
  sendWelcomeEmail = true;
  isSubmitting = false;

  PROJECT_MEMBER_ROLE_ICONS = PROJECT_MEMBER_ROLE_ICONS;
  PROJECT_MEMBER_ROLE_NAMES = PROJECT_MEMBER_ROLE_NAMES;
  PROJECT_MEMBER_ROLES = PROJECT_MEMBER_ROLES;

  ENTITY_TYPES = ENTITY_TYPES;

  fetchProjectMembers = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<ProjectMember>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
    };

    const where: WhereClause[] = [
      {
        field: 'entity.isServiceAccount',
        operator: '==',
        value: false,
      },
    ];

    if (this.searchText) {
      where.push({
        field: 'entity.name',
        operator: 'like',
        value: `%${this.searchText}%`,
      });
    }

    if (where.length) {
      options.where = where;
    }

    const result = await this.projectsService.listMembers(this.projectId, options);

    return result;
  };

  projectMembersDataSource = createDataSource(this.fetchProjectMembers);

  constructor(
    private projectsService: ProjectsService,
    private route: ActivatedRoute,
    public authService: AuthService,
    private msgSrvc: MessageService,
    private searchablePopoverService: SearchablePopoverService,
    private alertCtrl: AlertController,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  ngOnInit(): void {
    this.projectMember = this.authService.projects.find((p) => p.id === this.projectId);
    this.canEditMembers = this.authService.isTenantOwner || this.projectMember?.role === PROJECT_MEMBER_ROLES.OWNER;

    this.loadProjectMembers();
  }

  async loadProjectMembers() {
    this.isLoading = true;

    await this.projectMembersDataSource.firstPage();

    this.isLoading = false;
  }

  async changeRole(projectMember: ProjectMember) {
    this.searchablePopoverService.projectMemberRoles({
      event,
      includeOwner: this.canEditMembers,
      selectedRole: projectMember.role,
      callback: async (id) => {
        try {
          await this.projectsService.updateMember(this.projectId, projectMember.entityId, { role: id });

          this.loadProjectMembers();
        } catch (error) {
          this.msgSrvc.showError('Failed to change role');
        }
      },
    });
  }

  handleSearch(event: any) {
    this.searchText = event.target.value;
    this.loadProjectMembers();
  }

  async openAddMemberModal() {
    this.newMember = undefined;
    this.newMemberEntityType = undefined;
    this.newMemberRole = PROJECT_MEMBER_ROLES.AGENT;
    this.sendWelcomeEmail = true;

    await this.addMemberModal.present();
  }

  isValidNewMember(): boolean {
    return Boolean(this.newMember && this.newMemberRole);
  }

  async submit() {
    this.isSubmitting = true;

    try {
      await this.projectsService.addMember(
        this.projectId,
        {
          userId: this.newMember?.type === ENTITY_TYPES.USER ? this.newMember?.id : undefined,
          userGroupId: this.newMember?.type === ENTITY_TYPES.USER_GROUP ? this.newMember?.id : undefined,
          role: this.newMemberRole,
        },
        this.sendWelcomeEmail,
      );

      this.addMemberModal.dismiss();

      await this.loadProjectMembers();
    } catch (error) {
      this.msgSrvc.showError('Failed to add member');
    } finally {
      this.isSubmitting = false;
    }
  }

  async showMemberOptions() {
    this.searchablePopoverService.entities({
      event,
      isExternal: false,
      callback: async (_id: string, _name: string, object: any) => {
        this.newMember = object;
      },
    });
  }

  async showRoleOptions() {
    this.searchablePopoverService.projectMemberRoles({
      event,
      includeOwner: this.canEditMembers,
      selectedRole: this.newMemberRole ?? PROJECT_MEMBER_ROLES.STANDARD,
      callback: async (id) => {
        this.newMemberRole = id;
      },
    });
  }

  async removeMember(entityId: string) {
    const alert = await this.alertCtrl.create({
      header: 'Remove member?',
      subHeader: 'This will remove their access to the project.',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Remove',
          handler: async () => {
            try {
              await this.projectsService.removeMember(this.projectId, entityId);
              this.msgSrvc.show('Member removed from project');
              await this.loadProjectMembers();
            } catch (error) {
              this.msgSrvc.showError('Failed to remove member');
            }
          },
        },
      ],
    });

    await alert.present();
  }
}
